<template>
  <Loader v-if="bigLoading" />
  <div class="container-fluid" v-else>
    <div class="row">
      <div class="col">
        <form @submit.prevent="getData">
          <div class="card">
            <div class="card-body">
              <div class="row">
                <div class="col-lg-4 col-md-12 mb-3">
                  <label class="form-label" for="picker">
                    {{ $t("gpool.form_date") }}
                  </label>
                  <date-range-picker
                    ref="picker"
                    :timePicker="true"
                    :timePicker24Hour="true"
                    :time-picker-increment="1"
                    v-model="dateRange"
                    format="dd-MM-yyyy HH:mm:ss"
                    style="display: block"
                  >
                    <template v-slot:input="picker" style="min-width: 350px">
                      {{ picker.startDate | date }} -
                      {{ picker.endDate | date }}
                    </template>
                  </date-range-picker>
                </div>
                <div class="col-lg-4 col-md-12 mb-3">
                  <label class="form-label" for="sites">
                    {{ $t("gpool.form_site") }}
                  </label>
                  <select
                    class="form-control"
                    name="sites"
                    id="sites"
                    required
                    v-model="filter.siteid"
                  >
                    <option :value="site.id" v-for="site in sites">
                      {{ site.name }} - {{ site.domain }}
                    </option>
                  </select>
                </div>
                <div class="col-lg-auto col-md-12 mb-3">
                  <label class="form-label" for="currencies">
                    {{ $t("gpool.form_currency") }}
                  </label>
                  <select
                    class="form-control"
                    name="currencies"
                    id="currencies"
                    v-model="currencyId"
                  >
                    <option :value="currency.id" v-for="currency in currencies">
                      {{ currency.name }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="row">
                <div class="col-lg-3 col-md-12 mb-3">
                  <label class="form-label" for="categories">
                    {{ $t("gpool.form_category") }}
                  </label>
                  <select
                    class="form-control"
                    name="categories"
                    id="categories"
                    v-model="selected_category"
                  >
                    <option value="">
                      {{ $t("gpool.form_all") }}
                    </option>
                    <option :value="category.id" v-for="category in categories">
                      {{ category.name }}
                    </option>
                  </select>
                </div>
                <div class="col-lg-3 col-md-12 mb-3">
                  <label class="form-label" for="companies">
                    {{ $t("gpool.form_company") }}
                  </label>
                  <select
                    class="form-control"
                    name="companies"
                    id="companies"
                    v-model="selected_company"
                    :readonly="!selected_category"
                    :disabled="!selected_category"
                  >
                    <template v-if="selected_category">
                      <option value="">
                        {{ $t("gpool.form_all") }}
                      </option>
                      <option
                        :value="company.id"
                        v-for="company in filteredCompanies"
                      >
                        {{ company.name }}
                      </option>
                    </template>
                    <option v-else selected disabled value="">
                      {{ $t("gpool.form_select_category") }}
                    </option>
                  </select>
                </div>
                <div class="col-lg-3 col-md-12 mb-3">
                  <label class="form-label" for="games">
                    {{ $t("gpool.form_game") }}
                  </label>
                  <select
                    class="form-control"
                    name="games"
                    id="games"
                    v-model="selected_game"
                    :readonly="!selected_company"
                    :disabled="!selected_company"
                  >
                    <template v-if="selected_company">
                      <option value="">
                        {{ $t("gpool.form_all") }}
                      </option>
                      <option :value="game.id" v-for="game in filteredGames">
                        {{ game.name }}
                      </option>
                    </template>
                    <option value="" selected disabled v-else>
                      {{ $t("gpool.form_select_company") }}
                    </option>
                  </select>
                </div>
                <div class="col-lg-1 col-md-12 mb-3">
                  <label class="form-label" for=""> &nbsp; </label>
                  <button
                    type="submit"
                    class="btn btn-primary"
                    style="display: block"
                  >
                    <i class="fa fa-magnifying-glass"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
    <div class="row" v-if="!loading && !noData">
      <div class="col">
        <div class="card">
          <div class="card-body">
            <div class="row">
              <StatBadge
                :text="$t('gpool.table_in')"
                :fs="2"
                type="success"
                :number="totals.plus.in + totals.minus.in"
              />
              <StatBadge
                :text="$t('gpool.table_out')"
                :fs="2"
                type="danger"
                :number="totals.plus.out + totals.minus.out"
              />
              <StatBadge
                :text="$t('gpool.table_profit')"
                :fs="2"
                type="primary"
                :number="totals.plus.profit + totals.minus.profit"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <Loader v-if="loading" />
    <div class="row" v-show="!loading" v-if="!noData">
      <div class="col">
        <div class="card">
          <div class="card-header">
            <h5 class="card-title">
              {{ $t("gpool.most_profited", { amount: limit }) }}
            </h5>
          </div>
          <div class="card-body">
            <canvas id="profitChart"></canvas>
            <div v-if="!mostProfited.length">
              {{ $t("gpool.no_data") }}
            </div>
            <table
              class="table table-bordered table-striped mt-2"
              v-if="mostProfited.length"
            >
              <thead>
                <tr>
                  <th>{{ $t("gpool.table_name") }}</th>
                  <th>{{ $t("gpool.table_in") }}</th>
                  <th>{{ $t("gpool.table_out") }}</th>
                  <th>{{ $t("gpool.table_profit") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in mostProfited" :key="item.id">
                  <td>{{ item.name }}</td>
                  <td>{{ item.in | moneyFormatter }}</td>
                  <td>{{ item.out | moneyFormatter }}</td>
                  <td>{{ item.profit | moneyFormatter }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div class="col">
        <div class="card">
          <div class="card-header">
            <h5 class="card-title">
              {{ $t("gpool.most_lost", { amount: limit }) }}
            </h5>
          </div>
          <div class="card-body">
            <canvas id="lostChart"></canvas>
            <div v-if="!mostLost.length">
              {{ $t("gpool.no_data") }}
            </div>
            <table
              class="table table-bordered table-striped mt-2"
              v-if="mostLost.length"
            >
              <thead>
                <tr>
                  <th>{{ $t("gpool.table_name") }}</th>
                  <th>{{ $t("gpool.table_in") }}</th>
                  <th>{{ $t("gpool.table_out") }}</th>
                  <th>{{ $t("gpool.table_profit") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in mostLost" :key="item.id">
                  <td>{{ item.name }}</td>
                  <td>{{ item.in | moneyFormatter }}</td>
                  <td>{{ item.out | moneyFormatter }}</td>
                  <td>{{ item.profit | moneyFormatter }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    <div class="nodata" v-else>
      {{ $t("gpool.no_data") }}
    </div>
    <div class="row" v-if="!loading && !noData && list.length">
      <div class="col">
        <div class="card">
          <div class="card-body">
            <div class="row">
              <div class="col text-end mb-3">
                <button class="btn btn-success" @click="getExcel">
                  <i class="fa fa-file-excel"></i>&nbsp; Excel
                </button>
              </div>
            </div>
            <AgGridVue
              style="height: 500px; padding: 15px"
              class="ag-theme-alpine"
              :columnDefs="columns"
              :rowData="list"
              :pagination="true"
              @grid-ready="onGridReady"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
var MPChart, MLChart;
import { mapActions, mapGetters } from "vuex";
import DateRangePicker from "vue2-daterange-picker";
import "vue2-daterange-picker/dist/vue2-daterange-picker.css";
import moment from "moment";
import currencies from "../data/currencies.json";
import Loader from "../components/Loader.vue";
import StatBadge from "../components/StatBadge.vue";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import { AgGridVue } from "ag-grid-vue";
export default {
  name: "Stats",
  components: { DateRangePicker, Loader, StatBadge, AgGridVue },
  filters: {
    date: function (value) {
      return moment(value).format("DD.MM.YYYY HH:mm");
    },
  },
  watch: {
    selected_category() {
      this.selected_company = "";
      this.selected_game = "";
    },
    selected_company() {
      this.selected_game = "";
    },
    loading(newv) {
      if (!newv && !this.noData) {
        var self = this;
        setTimeout(() => {
          self.setChart();
        }, 100);
      }
    },
  },
  computed: {
    ...mapGetters("site", ["sites"]),
    ...mapGetters("category", ["categories"]),
    ...mapGetters("company", ["companies"]),
    ...mapGetters("game", ["games"]),
    ...mapGetters("stat", [
      "sum",
      "list",
      "loading",
      "bigLoading",
      "mostProfited",
      "mostLost",
      "limit",
      "noData",
      "totals",
    ]),
    filteredCompanies() {
      return this.companies.filter(
        (item) => item.category_id == this.selected_category
      );
    },
    filteredGames() {
      return this.games.filter(
        (item) => item.company_id == this.selected_company
      );
    },
  },
  async mounted() {
    await this.setLoading(true);
    await this.setBigLoading(true);

    await this.getSites();
    if (this.sites.length > 0) this.filter.siteid = this.sites[0].id;
    await this.getCategories();
    await this.getCompanies();
    await this.getGames();
    await this.setBigLoading(false);
    await this.getData();
  },
  methods: {
    getExcel() {
      this.api.exportDataAsCsv();
    },
    onGridReady(params) {
      this.api = params.api;
      this.columnApi = params.columnApi;
    },
    setChart() {
      if (MPChart) MPChart.destroy();
      if (MLChart) MLChart.destroy();

      if (this.mostProfited.length) {
        const profit = document.getElementById("profitChart");
        if (profit) profit.style.display = "block";

        MPChart = new Chart(profit, {
          type: "pie",
          data: {
            labels: this.mostProfited.map((item) => item.name),
            datasets: [
              {
                data: this.mostProfited.map((item) => item.profitPercent),
                backgroundColor: this.colors,
              },
            ],
          },
          options: {
            plugins: {
              tooltip: {
                callbacks: {
                  label: function (tooltipItem, data) {
                    return tooltipItem.formattedValue + "%";
                  },
                },
              },
            },
          },
        });
      } else {
        if (document.getElementById("profitChart"))
          document.getElementById("profitChart").style.display = "none";
      }

      if (this.mostLost.length) {
        const lost = document.getElementById("lostChart");
        if (lost) lost.style.display = "block";
        MLChart = new Chart(lost, {
          type: "pie",
          data: {
            labels: this.mostLost.map((item) => item.name),
            datasets: [
              {
                data: this.mostLost.map((item) => item.profitPercent),
                backgroundColor: this.colors,
              },
            ],
          },
          options: {
            plugins: {
              tooltip: {
                callbacks: {
                  label: function (tooltipItem, data) {
                    return tooltipItem.formattedValue + "%";
                  },
                },
              },
            },
          },
        });
      } else {
        if (document.getElementById("lostChart"))
          document.getElementById("lostChart").style.display = "none";
      }
    },
    async getData() {
      this.setLoading(true);
      this.setNoData(false);
      this.setList([]);
      if (
        this.selected_category &&
        this.selected_company &&
        this.selected_game
      ) {
        this.filter.providerid = this.selected_company;
        this.filter.gameid = this.selected_game;
      } else if (this.selected_category && this.selected_company) {
        this.filter.gameid = "";
        this.filter.providerid = this.selected_company;
      } else if (this.selected_category) {
        this.filter.gameid = "";
        this.filter.providerid = this.companies
          .filter((comp) => comp.category_id == this.selected_category)
          .map((a) => a.id);
      } else {
        this.filter.gameid = "";
        this.filter.providerid = "";
      }

      await this.getStatSum({
        filter: {
          ...this.dateRange,
          ...this.filter,
        },
        currencyId: this.currencyId,
        refs: {
          selected_category: this.selected_category,
          selected_company: this.selected_company,
          selected_game: this.selected_game,
        },
      });
      // await this.getStatList({
      //   filter: {
      //     ...this.dateRange,
      //     ...this.filter
      //   },
      //   currencyId: 1
      // });
    },
    ...mapActions("stat", [
      "getStatList",
      "getStatSum",
      "setLoading",
      "setBigLoading",
      "setNoData",
      "setList",
    ]),
    ...mapActions("site", ["getSites"]),
    ...mapActions("category", ["getCategories"]),
    ...mapActions("company", ["getCompanies"]),
    ...mapActions("game", ["getGames"]),
  },
  data() {
    return {
      currencies,
      dateRange: {
        startDate: moment().subtract(7, "days").startOf("day").unix() * 1000,
        endDate: moment().endOf("day").unix() * 1000,
      },
      filter: {
        siteid: "",
        providerid: "",
        gameid: "",
      },
      selected_category: "",
      selected_company: "",
      selected_game: "",
      currencyId: 2,
      api: null,
      columnApi: null,
      columns: [
        {
          headerName: "ID",
          field: "id",
          required: true,
        },
        {
          headerName: this.$t("gpool.table_name"),
          field: "name",
          required: true,
          sortable: true,
        },
        {
          headerName: this.$t("gpool.table_in"),
          field: "in",
          required: true,
          sortable: true,
          cellRenderer: (row) => {
            return Intl.NumberFormat("tr", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(row.value);
          },
          comparator: (valueA, valueB) => parseInt(valueA) - parseInt(valueB),
        },
        {
          headerName: this.$t("gpool.table_out"),
          field: "out",
          required: true,
          sortable: true,
          cellRenderer: (row) => {
            return Intl.NumberFormat("tr", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(row.value);
          },
          comparator: (valueA, valueB) => parseInt(valueA) - parseInt(valueB),
        },
        {
          headerName: this.$t("gpool.table_profit"),
          field: "profit",
          required: true,
          sortable: true,
          cellRenderer: (row) => {
            return Intl.NumberFormat("tr", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(row.value);
          },
          comparator: (valueA, valueB) => parseInt(valueA) - parseInt(valueB),
        },
        {
          headerName: this.$t("gpool.table_percent"),
          field: "profitPercent",
          required: true,
          sortable: true,
          comparator: (valueA, valueB) => parseInt(valueA) - parseInt(valueB),
          cellRenderer: (row) => {
            return row.value + "%";
          },
        },
      ],
      colors: [
        "#FF5733",
        "#00A2E8",
        "#FFC300",
        "#8B008B",
        "#00FF7F",
        "#FF1493",
        "#00CED1",
        "#FFD700",
        "#8A2BE2",
        "#00FF21",
      ],
    };
  },
};
</script>



// WEBPACK FOOTER //
// src/pages/Stats.vue